import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Sparkles,
  Clock,
  Flame,
  Maximize,
  Minimize,
  RotateCcw,
  Save,
  X,
  CheckCircle,
  TrendingUp,
  Award,
  Search,
  Lightbulb,
  Share2
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { collegeJourneyProgressService, collegeJourneyService } from '../../lib/college-journey-service';
import toast from 'react-hot-toast';
import ShareButton from '../Sharing/ShareButton';

const FreewriteInterface = ({
  userId,
  selectedDate = new Date().toISOString().split('T')[0],
  existingContent = '',
  existingPrompt = '',
  lastModified = null,
  onSave,
  onSessionComplete,
  onToggleSidebar
}) => {
  // Core state
  const [content, setContent] = useState(existingContent);
  const [prompt, setPrompt] = useState(existingPrompt);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [_sessionCompletedAt, setSessionCompletedAt] = useState(null);
  
  // Progressive disclosure state
  const [showControls, setShowControls] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  
  // Session tracking
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [writingStreak, setWritingStreak] = useState(0);
  const [_lastTypeTime, setLastTypeTime] = useState(null);
  const [showFlowReminder, setShowFlowReminder] = useState(false);
  
  // Focus mode
  const [isFocusMode, setIsFocusMode] = useState(false);
  
  // Writing Help panel
  const [showWritingHelp, setShowWritingHelp] = useState(false);

  
  // Conclusion modal
  const [showConclusionModal, setShowConclusionModal] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [extractedIdeas, setExtractedIdeas] = useState([]);
  
  // Refs
  const textareaRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const flowReminderTimeoutRef = useRef(null);
  
  // Update counts
  const updateCounts = useCallback((text) => {
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    const chars = text.length;
    
    setWordCount(words);
    setCharCount(chars);
    
    // Progressive disclosure - show controls after 20 characters
    if (chars > 20 && !showControls) {
      setShowControls(true);
    }
    
    // Check for session completion triggers
    if (words >= 500 && !showConclusionModal) {
      // Could trigger conclusion modal here
    }
  }, [showControls, showConclusionModal]);
  
  // Calculate writing streak
  const calculateWritingStreak = useCallback(async () => {
    if (!userId) return 0;
    
    try {
      // Get all freewrite dates for this user, ordered by date descending
      const { data: freewrites } = await supabase
        .from('college_journey_freewrites')
        .select('date')
        .eq('user_id', userId)
        .order('date', { ascending: false });
      
      if (!freewrites || freewrites.length === 0) return 0;
      
      // Convert dates and find consecutive days
      const dates = freewrites.map(f => new Date(f.date + 'T00:00:00'));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let streak = 0;
      let currentDate = new Date(today);
      
      // Check if there's a write today or yesterday (to account for timezone/timing)
      const hasRecentWrite = dates.some(date => {
        const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));
        return diffDays <= 1;
      });
      
      if (!hasRecentWrite) return 0;
      
      // Count consecutive days backwards from today
      for (let i = 0; i < dates.length; i++) {
        const writeDate = dates[i];
        const diffDays = Math.floor((currentDate - writeDate) / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) {
          // Found write for current date
          streak++;
          currentDate.setDate(currentDate.getDate() - 1);
        } else if (diffDays === 1 && i === 0) {
          // First check: if today's write doesn't exist, check yesterday
          currentDate.setDate(currentDate.getDate() - 1);
          if (Math.floor((currentDate - writeDate) / (1000 * 60 * 60 * 24)) === 0) {
            streak++;
            currentDate.setDate(currentDate.getDate() - 1);
          } else {
            break;
          }
        } else if (diffDays > 1) {
          // Gap in writing days
          break;
        }
      }
      
      return streak;
    } catch (error) {
      console.error('Error calculating writing streak:', error);
      return 0;
    }
  }, [userId]);

  // Initialize
  useEffect(() => {
    if (existingContent) {
      setContent(existingContent);
      updateCounts(existingContent);
      // If we have existing content, this is a saved document - not an active session
      setIsSessionActive(false);
      // Use real last saved time from database if available
      if (lastModified) {
        setLastSaved(new Date(lastModified).getTime());
      } else {
        // Default to now if no previous save time
        setLastSaved(Date.now());
      }
    } else {
      // New document - session is not active until they start typing
      setIsSessionActive(false);
    }
    
    if (existingPrompt) {
      setPrompt(existingPrompt);
    }
    
    // Calculate and set writing streak
    calculateWritingStreak().then(setWritingStreak);
    
    // Auto-focus textarea when component loads
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  }, [existingContent, existingPrompt, lastModified, updateCounts, calculateWritingStreak]);
  
  // Handle content change
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    updateCounts(newContent);
    
    // Mark session as active when they start typing
    if (!isSessionActive && newContent.length > 0) {
      setIsSessionActive(true);
      setSessionCompletedAt(null); // Clear any previous completion time
    }
    
    // Track session start
    if (!sessionStartTime && newContent.length > 0) {
      setSessionStartTime(Date.now());
      startSessionTimer();
    }
    
    // Update last type time for flow state tracking
    setLastTypeTime(Date.now());
    
    // Flow state protection - hide reminder when typing
    if (showFlowReminder) {
      setShowFlowReminder(false);
    }
    
    // Reset flow reminder timer
    if (flowReminderTimeoutRef.current) {
      clearTimeout(flowReminderTimeoutRef.current);
    }
    
    // Show gentle reminder after 30 seconds of inactivity (only if content exists)
    if (newContent.length > 50) {
      flowReminderTimeoutRef.current = setTimeout(() => {
        if (isFocused) {
          setShowFlowReminder(true);
          // Hide reminder after 3 seconds
          setTimeout(() => setShowFlowReminder(false), 3000);
        }
      }, 30000);
    }
    
    // Auto-save debounce
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave(newContent);
    }, 2000); // Save after 2 seconds of inactivity
  };
  
  // Auto-save functionality
  const handleAutoSave = async (contentToSave) => {
    if (!contentToSave.trim() || !userId) return;
    
    setIsSaving(true);
    try {
      // Check for existing freewrite for this date
      const { data: existing } = await supabase
        .from('college_journey_freewrites')
        .select('id')
        .eq('user_id', userId)
        .eq('date', selectedDate)
        .single();
      
      const saveData = {
        user_id: userId,
        date: selectedDate,
        content: contentToSave,
        word_count: contentToSave.trim().split(/\s+/).filter(w => w.length > 0).length,
        last_updated: new Date().toISOString()
      };
      
      let result;
      if (existing) {
        // Update existing
        result = await supabase
          .from('college_journey_freewrites')
          .update(saveData)
          .eq('id', existing.id);
      } else {
        // Create new
        result = await supabase
          .from('college_journey_freewrites')
          .insert([{ ...saveData, created_at: new Date().toISOString() }]);
      }
      
      if (result.error) throw result.error;

      // Recalculate streak after successful save (in case this completed a new day)
      calculateWritingStreak().then(setWritingStreak);

      // Call onSave callback to notify parent component
      if (onSave) {
        onSave(saveData);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    } finally {
      setIsSaving(false);
      setLastSaved(Date.now());
    }
  };
  
  // Generate prompt with AI personalization and caching
  const generatePrompt = async () => {
    setIsGeneratingPrompt(true);
    try {
      // Debug: Check if service and method exist
      console.log('Progress service exists:', !!collegeJourneyProgressService);
      console.log('getCachedPrompts exists:', typeof collegeJourneyProgressService?.getCachedPrompts);
      
      // First check for cached prompts
      let cachedPrompts = null;
      try {
        if (collegeJourneyProgressService && typeof collegeJourneyProgressService.getCachedPrompts === 'function') {
          cachedPrompts = await collegeJourneyProgressService.getCachedPrompts(userId);
          console.log('Cache check result:', cachedPrompts);
        } else {
          console.log('getCachedPrompts method not available, skipping cache check');
        }
      } catch (cacheError) {
        console.log('Cache check failed, continuing without cache:', cacheError);
      }
      
      if (cachedPrompts && Array.isArray(cachedPrompts) && cachedPrompts.length > 0) {
        // Use cached prompts
        console.log('📚 Using cached prompts');
        const randomPrompt = cachedPrompts[Math.floor(Math.random() * cachedPrompts.length)];
        setPrompt(randomPrompt);
        
        toast.success('Prompt generated from your personalized collection!');
        return;
      }
      
      // No cached prompts, generate new ones with AI
      console.log('🤖 Generating new personalized prompts');
      
      // Show loading message for initial generation
      toast.loading('Creating your personalized prompt collection... (future prompts will load instantly!)', {
        id: 'prompt-generation',
        duration: 10000
      });
      
      // Fetch student data for context
      const [studentProfile, assessmentData] = await Promise.all([
        collegeJourneyService.getAcademicProfile(userId),
        collegeJourneyService.getSavedAssessmentResponses(userId)
      ]);
      
      // Call API to generate prompts
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-freewrite-prompts',
          studentProfile: studentProfile || {},
          assessmentData: assessmentData || {},
          userId: userId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate prompts');
      }
      
      const result = await response.json();
      
      if (result.prompts && result.prompts.length > 0) {
        // Cache the prompts for future use
        await collegeJourneyProgressService.saveGeneratedPrompts(userId, result.prompts);
        
        // Use a random prompt from the generated ones
        const randomPrompt = result.prompts[Math.floor(Math.random() * result.prompts.length)];
        setPrompt(randomPrompt);
        
        // Dismiss loading toast and show success
        toast.dismiss('prompt-generation');
        toast.success(result.fallback ? 'Prompt generated!' : 'Your personalized prompt collection is ready! Future prompts will load instantly.');
      } else {
        // Fallback to hardcoded prompts if API fails
        const fallbackPrompts = [
          "Write about a moment when you surprised yourself with your own capabilities.",
          "Describe a time when you had to stand up for something you believed in, even when it was difficult.",
          "What's a skill you have that most people don't know about? How did you develop it?",
          "Write about a tradition in your family or community that has shaped who you are.",
          "Describe a problem you've noticed that you wish you could solve. What would your solution look like?"
        ];
        
        const newPrompt = fallbackPrompts[Math.floor(Math.random() * fallbackPrompts.length)];
        setPrompt(newPrompt);
        
        // Dismiss loading toast if it exists
        toast.dismiss('prompt-generation');
        toast.success('Prompt generated!');
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
      
      // Ultimate fallback
      const emergencyPrompts = [
        "Tell me about a moment that changed your perspective.",
        "What's something you're proud of that others might not notice?",
        "Describe a challenge you overcame in an unexpected way."
      ];
      
      const emergencyPrompt = emergencyPrompts[Math.floor(Math.random() * emergencyPrompts.length)];
      setPrompt(emergencyPrompt);
      
      // Dismiss loading toast if it exists
      toast.dismiss('prompt-generation');
      toast.error('Using backup prompts. Try again later for personalized suggestions.');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };
  
  // Copy prompt (currently unused but may be needed for future features)
  const _copyPrompt = () => {
    if (prompt) {
      navigator.clipboard.writeText(prompt);
      toast.success('Prompt copied to clipboard');
    }
  };
  
  // Session timer
  const startSessionTimer = () => {
    sessionTimerRef.current = setInterval(() => {
      setSessionDuration(prev => prev + 1);
    }, 1000); // Update every second
  };
  

  
  // Focus mode toggle
  const toggleFocusMode = useCallback(() => {
    setIsFocusMode(!isFocusMode);
    if (!isFocusMode) {
      // Enter focus mode
      document.body.style.overflow = 'hidden';
      textareaRef.current?.focus();
    } else {
      // Exit focus mode
      document.body.style.overflow = 'auto';
    }
  }, [isFocusMode]);
  

  
  // Handle ESC key for focus mode
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isFocusMode) {
        toggleFocusMode();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isFocusMode, toggleFocusMode]);
  
  // Show conclusion modal
  const showConclusion = async () => {
    // Mark session as completed
    const completionTime = Date.now();
    setIsSessionActive(false);
    setSessionCompletedAt(completionTime);
    setLastSaved(completionTime);
    
    // Calculate session stats
    const stats = {
      words: wordCount,
      duration: sessionDuration,
      streak: writingStreak,
      date: selectedDate,
      avgWordsPerMinute: sessionDuration > 0 ? Math.round(wordCount / (sessionDuration / 60)) : 0
    };
    
    setSessionStats(stats);
    
    // Extract key ideas (in production, this would use AI)
    if (content.length > 100) {
      const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 20);
      const ideas = sentences.slice(0, 3).map(s => s.trim());
      setExtractedIdeas(ideas);
    }
    
    setShowConclusionModal(true);
    
    // Save final content
    await handleAutoSave(content);
    
    if (onSessionComplete) {
      onSessionComplete(stats);
    }
  };
  
  // Close conclusion modal
  const closeConclusionModal = () => {
    setShowConclusionModal(false);
    // Could reset for new session or navigate elsewhere
  };
  
  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
      if (flowReminderTimeoutRef.current) {
        clearTimeout(flowReminderTimeoutRef.current);
      }
      document.body.style.overflow = 'auto';
    };
  }, []);
  
  // Format date for display
  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className={`relative h-full bg-stone-50 ${isFocusMode ? 'fixed inset-0 z-50 flex-col' : ''}`}>
      {/* Header - Left aligned, minimal design following guidelines */}
      {!isFocusMode && (
        <div className="flex-shrink-0 px-6 pt-2">
          <div className="max-w-none">
            {/* Clean header layout */}
            <div className="flex items-start justify-between mb-2">
              {/* Left - Title and Date */}
              <div>
                <p className="text-xs text-stone-600 mb-1">
                  {formatDateDisplay(selectedDate)}
                </p>
                <h1 className="text-xl font-bold text-slate-900">Today's Free Write</h1>
              </div>
              
              {/* Right - Session Stats */}
              <div className="flex items-center gap-6">
                {/* Writing Streak */}
                {writingStreak >= 3 && (
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-coral-50 text-coral-600 rounded-lg border border-coral-200">
                    <Flame className="w-4 h-4" />
                    <span className="text-sm font-medium">{writingStreak} day streak</span>
                  </div>
                )}
                
                {/* Word Count & Timer */}
                {charCount > 20 && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-stone-600">
                      <span className="font-semibold">{wordCount}</span>
                      <span className="ml-1 text-stone-500">words</span>
                    </div>
                    
                    {sessionDuration > 0 && (
                      <div className="flex items-center gap-1 text-stone-500">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">{formatDuration(sessionDuration)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Save Status */}
                <div className="flex items-center gap-1 text-xs">
                  {isSaving ? (
                    <>
                      <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-teal-600">Saving...</span>
                    </>
                  ) : lastSaved ? (
                    <>
                      <Save className="w-3 h-3 text-stone-400" />
                      <span className="text-stone-400">Auto-saved</span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content and Writing Help Container */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Content Area - Showcase style */}
        <div className={`flex-1 overflow-y-auto bg-stone-50 ${isFocusMode ? '' : 'flex flex-col'}`}>
        <div className="max-w-none px-4 md:px-6">
          {/* Prompt Section - Minimal and unobtrusive */}
          {!isFocusMode && (
            <div className={`mb-6 transition-all duration-500 ${
              charCount >= 50 ? 'opacity-25 pointer-events-none' : 'opacity-100'
            }`}>
              <button
                onClick={async () => {
                  if (!isPromptOpen && !prompt) {
                    setIsPromptOpen(true);
                    await generatePrompt();
                  } else {
                    setIsPromptOpen(!isPromptOpen);
                  }
                }}
                disabled={isGeneratingPrompt}
                className="flex items-center gap-2 text-xs text-teal-600 hover:text-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-expanded={isPromptOpen}
                aria-controls="prompt-content"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0"></div>
                <span className="font-medium">
                  {prompt ? 'Writing Prompt' : (isGeneratingPrompt ? 'Generating prompt...' : 'Get a Writing Prompt')}
                </span>
                {isPromptOpen ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>
            
              <div 
                id="prompt-content"
                className={`
                  overflow-hidden transition-all duration-300 ease-out
                  ${isPromptOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}
                `}
              >
                <div className="mt-3 p-4 bg-stone-50 rounded-lg border border-stone-200">
                  {prompt ? (
                    <>
                      <p className="text-slate-700 leading-relaxed mb-3 text-sm">
                        {prompt}
                      </p>
                      <button
                        onClick={generatePrompt}
                        disabled={isGeneratingPrompt}
                        className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1.5 text-xs font-medium disabled:opacity-50 border-2 border-slate-900"
                      >
                        <Sparkles className="w-3 h-3" />
                        {isGeneratingPrompt ? 'Generating...' : 'New Prompt'}
                      </button>
                    </>
                  ) : (
                    <div className="py-1">
                      <p className="text-slate-600 mb-3 text-sm">
                        Need inspiration? Generate a writing prompt to get started.
                      </p>
                      <button
                        onClick={generatePrompt}
                        disabled={isGeneratingPrompt}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-sm font-medium disabled:opacity-50 border-2 border-slate-900"
                      >
                        {isGeneratingPrompt ? 'Generating...' : 'Generate Writing Prompt'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Main Writing Area - Clean white card like showcase */}
          <div className={`relative ${isFocusMode ? 'min-h-screen px-8 py-12' : ''}`}>
            <div className={`bg-white rounded-xl p-4 md:p-8 min-h-[600px] border-2 transition-colors duration-200 ${
              isFocused ? 'border-teal-500' : 'border-slate-900'
            }`}>
              {/* Prompt header when expanded */}
              {isPromptOpen && prompt && !isFocusMode && (
                <div className="mb-6 pb-4 border-b border-stone-200">
                  <div className="flex items-center gap-2 text-xs text-teal-600 mb-3">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="font-medium">Writing Prompt</span>
                  </div>
                </div>
              )}
              
              <textarea
                ref={textareaRef}
                value={content}
                onChange={handleContentChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={content ? '' : "You don't have to finish. You just have to start."}
                className={`w-full h-full min-h-[500px] resize-none border-none outline-none text-base leading-relaxed text-slate-900 placeholder:text-stone-500 placeholder:italic bg-transparent focus-visible:outline-none
                  ${isFocusMode ? 'text-lg leading-8' : ''}
                `}
                style={{
                  fontFamily: "'Reddit Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                }}
              />
              
              {/* Bottom status bar - matches showcase */}
              <div className="mt-8 pt-6 border-t border-stone-200">
                <div className="flex items-center justify-between text-xs text-stone-600">
                  <div className="flex items-center gap-3">
                    <span>{wordCount} words</span>
                    {/* Compact progress indicator - mobile only */}
                    <span className="lg:hidden text-teal-600 font-medium">
                      {sessionDuration > 0 ? formatDuration(sessionDuration) : '0:00'}
                    </span>
                  </div>
                  <span>
                    {isSaving ? 'Saving...' : lastSaved ? 'Auto-saved just now' : 'Start typing to save automatically'}
                  </span>
                </div>

                {/* Mobile: Compact session controls - shown only when writing has started */}
                {wordCount > 50 && onToggleSidebar && !showConclusionModal && (
                  <div className="lg:hidden mt-4">
                    <button
                      onClick={async () => {
                        if (sessionTimerRef.current) {
                          clearInterval(sessionTimerRef.current);
                        }
                        setIsSessionActive(false);
                        await handleAutoSave(content);
                        if (onSave) {
                          onSave();
                        }
                        onToggleSidebar();
                        toast.success('Freewrite saved! Check Recent Activity.');
                      }}
                      className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-sm font-medium border-2 border-slate-900 flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Done Writing
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Flow State Reminder */}
            {showFlowReminder && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-stone-100 text-slate-600 px-4 py-2 rounded-full text-sm font-medium animate-fade-in border border-stone-200 shadow-sm">
                Keep the thoughts flowing...
              </div>
            )}
          </div>
        </div>
        </div>
        
        {/* Session Progress & Rules Sidebar - Hidden on mobile */}
        {!isFocusMode && (
          <div className="hidden lg:flex w-80 bg-stone-50 border-l border-stone-200 flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Session Progress Card */}
              <div className="bg-white rounded-xl border-2 border-teal-500 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-900">Session Progress</h3>
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-teal-600" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-stone-600 text-sm">Time Writing</span>
                    <span className="font-bold text-lg text-slate-900">
                      {sessionDuration > 0 ? formatDuration(sessionDuration) : '0:00'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-stone-600 text-sm">Words Generated</span>
                    <span className="font-bold text-lg text-slate-900">{wordCount}</span>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-stone-200">
                    <span className="text-stone-600 text-sm">Flow State</span>
                    <span className="text-teal-600 font-medium text-sm flex items-center gap-1">
                      {wordCount > 50 ? 'Complete' : 'Getting started'}
                      {wordCount > 50 && <CheckCircle className="w-4 h-4" />}
                    </span>
                  </div>

                  {/* DONE Button - Inside session stats */}
                  {wordCount > 50 && onToggleSidebar && !showConclusionModal && (
                    <div className="pt-4">
                      <button
                        onClick={async () => {
                          // Stop the session timer
                          if (sessionTimerRef.current) {
                            clearInterval(sessionTimerRef.current);
                          }

                          // Mark session as inactive
                          setIsSessionActive(false);

                          // Ensure content is saved
                          await handleAutoSave(content);

                          // Call onSave to refresh Recent Activity
                          if (onSave) {
                            onSave();
                          }

                          // Toggle the sidebar to show Recent Activity
                          onToggleSidebar();

                          toast.success('Freewrite saved! Check Recent Activity.');
                        }}
                        className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-sm font-medium border-2 border-slate-900 hover:scale-95 flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        DONE
                      </button>
                    </div>
                  )}
                </div>

                {wordCount > 100 && (
                  <div className="mt-6 pt-4 border-t border-stone-200 text-center">
                    <p className="text-teal-600 font-medium text-sm">Great freewriting session!</p>
                  </div>
                )}
              </div>

              {/* Freewriting Rules Card */}
              <div className="bg-teal-50 rounded-xl border border-teal-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Freewriting Rules</h3>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal-700 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm mb-1">Keep writing</h4>
                      <p className="text-stone-600 text-xs leading-relaxed">Don't stop, even if you run out of ideas</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal-700 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm mb-1">No editing</h4>
                      <p className="text-stone-600 text-xs leading-relaxed">Grammar and spelling don't matter yet</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal-700 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm mb-1">Follow thoughts</h4>
                      <p className="text-stone-600 text-xs leading-relaxed">Let your mind wander and explore</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal-700 font-bold text-sm">4</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm mb-1">Stay honest</h4>
                      <p className="text-stone-600 text-xs leading-relaxed">Write what you really think and feel</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Writing Help Panel */}
        {showWritingHelp && !isFocusMode && (
          <div className="w-80 bg-stone-50 border-l border-stone-200 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-stone-200 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-900">Writing Help</h3>
                <button
                  onClick={() => setShowWritingHelp(false)}
                  className="p-1 hover:bg-stone-100 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Freewriting Tips */}
              <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-azure-600" />
                  <div className="text-sm font-semibold text-azure-800">Stream of Consciousness</div>
                </div>
                <div className="space-y-2 text-sm text-azure-700">
                  <p>• Write continuously without stopping to edit</p>
                  <p>• Don't worry about grammar, spelling, or structure</p>
                  <p>• Let your thoughts flow naturally onto the page</p>
                  <p>• Set a timer for 10-15 minutes of focused writing</p>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-600" />
                  <div className="text-sm font-semibold text-stone-800">Reflection Prompts</div>
                </div>
                <div className="space-y-2 text-sm text-stone-700">
                  <p>• What did I learn about myself today?</p>
                  <p>• What challenged me recently and how did I respond?</p>
                  <p>• What am I curious about right now?</p>
                  <p>• What moments made me feel most like myself?</p>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Search className="w-4 h-4 text-emerald-600" />
                  <div className="text-sm font-semibold text-stone-800">Mining for Essays</div>
                </div>
                <div className="space-y-2 text-sm text-stone-700">
                  <p>• Look for moments of change or realization</p>
                  <p>• Notice recurring themes in your writing</p>
                  <p>• Circle specific stories that surprise you</p>
                  <p>• Ask "What does this reveal about who I am?"</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Action Cards Below Writing Area - Showcase style */}
      {charCount > 50 && !isFocusMode && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Writing Help Card */}
            <button
              onClick={() => setShowWritingHelp(!showWritingHelp)}
              className="bg-white rounded-xl border border-slate-300 p-4 text-left hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-900 transition-all duration-200"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-azure-500 flex-shrink-0"></div>
                <h3 className="text-sm font-bold text-slate-900">Writing Support</h3>
                <div className="ml-auto px-2 py-1 bg-azure-100 text-azure-700 rounded-lg text-xs font-medium uppercase tracking-wide">
                  TIPS
                </div>
              </div>
              <p className="text-xs text-stone-600">
                Get guidance on freewriting techniques and essay development
              </p>
            </button>

            {/* Ideas Extraction Card */}
            <button
              onClick={() => {
                const selection = window.getSelection().toString().trim();
                if (selection && selection.length > 10) {
                  const savedIdeas = JSON.parse(localStorage.getItem('essay-ideas') || '[]');
                  const newIdea = {
                    id: Date.now(),
                    text: selection,
                    date: new Date().toISOString(),
                    source: 'freewrite_selection'
                  };
                  const updatedIdeas = [newIdea, ...savedIdeas];
                  localStorage.setItem('essay-ideas', JSON.stringify(updatedIdeas));
                  toast.success('Selection saved as idea!');
                } else {
                  toast.error('Please select some text first (at least 10 characters)');
                }
              }}
              className="bg-white rounded-xl border border-slate-300 p-4 text-left hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-900 transition-all duration-200"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-gold-500 flex-shrink-0"></div>
                <h3 className="text-sm font-bold text-slate-900">Extract Ideas</h3>
                <div className="ml-auto px-2 py-1 bg-gold-100 text-gold-700 rounded-lg text-xs font-medium uppercase tracking-wide">
                  SELECT
                </div>
              </div>
              <p className="text-xs text-stone-600">
                Highlight text to save promising ideas for future essays
              </p>
            </button>
          </div>

          {/* Done Writing Button */}
          <div className="mt-4 flex justify-center">
            <button
              onClick={showConclusion}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-sm font-medium border-2 border-slate-900 hover:scale-95"
            >
              Complete Session
            </button>
          </div>
        </div>
      )}
      
      {/* Focus Mode Exit Button */}
      {isFocusMode && (
        <button
          onClick={toggleFocusMode}
          className="fixed top-6 right-6 p-2 bg-stone-100 text-slate-600 rounded-full hover:bg-stone-200 transition-colors"
          title="Exit Focus Mode (ESC)"
        >
          <X className="w-5 h-5" />
        </button>
      )}
      
      {/* Conclusion Modal */}
      {showConclusionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/30 animate-fade-in">
          <div className="bg-stone-100 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-teal-600 p-6 text-white rounded-t-xl">
              <div className="flex items-center justify-center mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-center mb-1">
                Freewriting Session Complete!
              </h2>
              <p className="text-center text-teal-100 text-sm">
                {sessionStats?.words || 0} words captured • {sessionStats ? formatDuration(sessionStats.duration) : '0:00'} of uninterrupted flow
              </p>
            </div>
            
            {/* Stats Grid */}
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-semibold text-slate-900 mb-0.5">
                    {sessionStats?.words || 0}
                  </div>
                  <div className="text-xs text-slate-600">Words Written</div>
                </div>
                
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-semibold text-slate-900 mb-0.5">
                    {sessionStats ? formatDuration(sessionStats.duration) : '0:00'}
                  </div>
                  <div className="text-xs text-slate-600">Time Spent</div>
                </div>
                
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-semibold text-slate-900 mb-0.5">
                    {sessionStats?.avgWordsPerMinute || 0}
                  </div>
                  <div className="text-xs text-slate-600">Words/Minute</div>
                </div>
                
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-semibold text-slate-900 mb-0.5">
                    {sessionStats?.streak || 0}
                  </div>
                  <div className="text-xs text-slate-600">Writing Streak</div>
                </div>
              </div>
              
              {/* Extracted Ideas */}
              {extractedIdeas.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-base font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-violet-600" />
                    Key Themes from Your Writing
                  </h3>
                  <div className="space-y-2">
                    {extractedIdeas.map((idea, index) => (
                      <div
                        key={index}
                        className="p-3 bg-violet-50 rounded-lg border border-violet-100"
                      >
                        <p className="text-slate-700 text-sm leading-relaxed">
                          {idea}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Motivational Message */}
              <div className="bg-amber-50 rounded-lg p-4 mb-6 border border-amber-100">
                <div className="flex items-start gap-2.5">
                  <Award className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900 mb-1 text-sm">
                      Keep Up the Momentum!
                    </h4>
                    <p className="text-amber-800 text-xs leading-relaxed">
                      You're building a strong writing habit. Regular practice like this will make your college essays flow more naturally and authentically.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={closeConclusionModal}
                  className="flex-1 px-4 py-2 bg-stone-100 text-slate-700 rounded-lg hover:bg-stone-200 transition-colors font-medium text-sm"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closeConclusionModal();
                    setContent('');
                    setWordCount(0);
                    setCharCount(0);
                    setShowControls(false);
                    setSessionDuration(0);
                    // Reset session state for new session
                    setIsSessionActive(false);
                    setSessionCompletedAt(null);
                    setLastSaved(null);
                    // Recalculate streak for new session
                    calculateWritingStreak().then(setWritingStreak);
                    // Auto-focus after starting new session
                    setTimeout(() => {
                      textareaRef.current?.focus();
                    }, 100);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm border-2 border-slate-900 hover:scale-95"
                >
                  Start New Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreewriteInterface;